import { useState, useCallback, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { FLAGS } from '../data/flags';

function shuffle(a){const b=[...a];for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];}return b;}
function makeOpts(c,all){return shuffle([c,...shuffle(all.filter(f=>f.name!==c.name)).slice(0,3)]);}

const TOTAL_ROUNDS = 15;

export default function MultiLobby({onBack, onJoinAsPlayer}){
  const [mode,setMode]    = useState('choose'); // choose | host | game | gameover
  const [code,setCode]    = useState('');
  const [players,setPlayers] = useState([]);
  const [status,setStatus] = useState('');
  const [roundN,setRoundN] = useState(0);
  const [currentFlag,setCurrentFlag] = useState(null);
  const [opts,setOpts]    = useState([]);
  const [answers,setAnswers] = useState([]);
  const [resolved,setResolved] = useState(false);
  const [roundResult,setRoundResult] = useState(null);
  const [finalScores,setFinalScores] = useState(null);
  const deck = useRef([]);

  const onMsg = useCallback((msg)=>{
    switch(msg.type){
      case 'SESSION_CREATED': setCode(msg.code); setMode('host'); deck.current=shuffle(FLAGS); break;
      case 'PLAYERS_UPDATE':  setPlayers(msg.players); break;
      case 'PLAYER_JOINED':   setStatus(msg.name+' joined!'); setTimeout(()=>setStatus(''),2500); break;
      case 'PLAYER_LEFT':     setStatus(msg.name+' left');    setTimeout(()=>setStatus(''),2500); break;
      case 'PLAYER_ANSWERED': setAnswers(prev=>[...prev,{name:msg.name,time:msg.time,allAnswered:msg.allAnswered}]); break;
      case 'ROUND_RESULT':    setRoundResult(msg); setResolved(true); break;
      case 'GAME_OVER':       setFinalScores(msg.final); setMode('gameover'); break;
      case 'BACK_TO_LOBBY':   setMode('host'); setRoundN(0); setCurrentFlag(null); setFinalScores(null); break;
    }
  },[]);

  const {send} = useWebSocket(onMsg);

  const createSession = useCallback(()=> send('CREATE_SESSION'), [send]);

  const startGame = useCallback(()=>{
    deck.current = shuffle(FLAGS);
    send('START_GAME',{game:'flagflash'});
    setRoundN(0); setCurrentFlag(null); setAnswers([]); setResolved(false); setRoundResult(null);
    setMode('game');
  },[send]);

  const pushRound = useCallback(()=>{
    const idx = roundN;
    if(idx >= deck.current.length) return;
    const f = deck.current[idx];
    const o = makeOpts(f, FLAGS);
    setCurrentFlag(f); setOpts(o); setAnswers([]); setResolved(false); setRoundResult(null);
    setRoundN(idx+1);
    send('PUSH_QUESTION',{
      question:{ type:'flag', flag:f.flag, text:'Which country is this flag?', opts:o.map(x=>x.name) },
      timeLimit:8000
    });
  },[roundN, send]);

  const resolveRound = useCallback(()=>{
    if(!currentFlag) return;
    send('RESOLVE_ROUND',{correctAnswer:currentFlag.name, game:'flagflash'});
  },[currentFlag, send]);

  const endGame    = useCallback(()=> send('END_GAME'),     [send]);
  const kickPlayer = useCallback((name)=> send('KICK_PLAYER',{name}), [send]);
  const returnLobby= useCallback(()=> send('RETURN_LOBBY'), [send]);

  const sorted = roundResult
    ? [...roundResult.results].sort((a,b)=>b.score-a.score)
    : [...players].sort((a,b)=>b.score-a.score);

  /* ── CHOOSE ROLE ── */
  if(mode==='choose') return(
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title blue">MULTIPLAYER</div>
      </div>
      <div className="lobby-wrap"><div className="lobby-card" style={{textAlign:'center',gap:18}}>
        <div style={{fontSize:'0.55rem',letterSpacing:'0.2em',color:'#4a6a9e',textTransform:'uppercase'}}>Choose your role</div>
        <button className="btn-blue" onClick={createSession}>🖥️ HOST — Create Session</button>
        <button className="btn-purple" onClick={onJoinAsPlayer}>📱 PLAYER — Join with Code</button>
        <p style={{fontSize:'0.72rem',color:'#4a6a9e',lineHeight:1.5}}>
          Host creates the session and controls the game.<br/>
          Players join on their phones using the 4-letter code.
        </p>
      </div></div>
    </div>
  );

  /* ── HOST LOBBY ── */
  if(mode==='host') return(
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title blue">HOST LOBBY</div>
        <div className="g-pills"><div className="g-pill b"><span className="g-plbl">Players</span><span className="g-pval">{players.length}</span></div></div>
      </div>
      <div className="lobby-wrap"><div className="lobby-card">
        <div className="lobby-code-label">Your session code</div>
        <div className="lobby-code">{code}</div>
        <p style={{fontSize:'0.72rem',color:'#4a6a9e',textAlign:'center'}}>
          Players visit <strong style={{color:'#38bdf8'}}>games.housemonk.co.uk</strong> and tap <strong style={{color:'#38bdf8'}}>Join Game</strong>
        </p>
        {status&&<p style={{fontSize:'0.74rem',color:'#4ade80',textAlign:'center',animation:'pop .2s ease'}}>{status}</p>}
        <div className="lobby-players">
          <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'#4a6a9e',textTransform:'uppercase',marginBottom:4}}>
            Players ({players.length})
          </div>
          {players.length===0&&<div className="lobby-waiting">Waiting for players to join...</div>}
          {players.map((p,i)=>(
            <div key={i} className="lobby-player">
              <div className="lobby-player-dot"/>
              <span className="lobby-player-name">{p.name}</span>
              <button onClick={()=>kickPlayer(p.name)} style={{background:'transparent',border:'none',color:'#f87171',cursor:'pointer',fontSize:'0.7rem'}}>kick</button>
            </div>
          ))}
        </div>
        <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase',textAlign:'center'}}>Game: 🚩 Flag Flash · {TOTAL_ROUNDS} rounds</div>
        <button className="btn-blue" onClick={startGame} disabled={players.length===0}>START GAME</button>
      </div></div>
    </div>
  );

  /* ── GAME IN PROGRESS ── */
  if(mode==='game') return(
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={returnLobby}>← Lobby</button>
        <div className="g-title blue">HOST · FLAG FLASH</div>
        <div className="g-pills">
          <div className="g-pill b"><span className="g-plbl">Round</span><span className="g-pval">{roundN}/{TOTAL_ROUNDS}</span></div>
          <div className="g-pill b"><span className="g-plbl">Players</span><span className="g-pval">{players.length}</span></div>
        </div>
      </div>
      <div className="game-body" style={{maxWidth:640,alignItems:'center'}}>

        {/* Not yet started a round */}
        {!currentFlag&&<>
          <p style={{fontSize:'0.84rem',color:'#8ba3cc',textAlign:'center'}}>
            {players.length} player{players.length!==1?'s':''} connected.<br/>
            First correct answer gets <strong style={{color:'#f5c518'}}>15 pts</strong> · Others correct get <strong style={{color:'#38bdf8'}}>10 pts</strong>
          </p>
          <button className="btn-blue" onClick={pushRound}>Push Round 1 →</button>
        </>}

        {/* Round in progress */}
        {currentFlag&&<>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%'}}>
            <span style={{fontSize:'0.7rem',color:'#8ba3cc'}}>Round {roundN} of {TOTAL_ROUNDS}</span>
            <span style={{fontSize:'0.7rem',color:'#8ba3cc'}}>{answers.length}/{players.length} answered</span>
          </div>

          <div className="flag-big">{currentFlag.flag}</div>

          <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
            {opts.map((o,i)=>(
              <div key={i} style={{padding:'6px 14px',borderRadius:7,background:'#0f1e38',border:'1px solid #1e3a6e',fontSize:'0.78rem',color:'#8ba3cc'}}>{o.name}</div>
            ))}
          </div>

          {/* Live answer feed */}
          {answers.length>0&&<div style={{width:'100%',background:'#0a1628',borderRadius:9,padding:'8px 12px'}}>
            <div style={{fontSize:'0.52rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase',marginBottom:5}}>Answers in</div>
            {answers.map((a,i)=>(
              <div key={i} style={{display:'flex',gap:8,alignItems:'center',padding:'3px 0',borderBottom:'1px solid #0f1e38'}}>
                <span style={{fontSize:'0.78rem',color:'#e8edf8',flex:1}}>{a.name}</span>
                <span style={{fontSize:'0.68rem',color:'#8ba3cc'}}>{(a.time/1000).toFixed(2)}s</span>
              </div>
            ))}
          </div>}

          {/* Reveal / Next controls */}
          {!resolved&&<button className="btn-blue" onClick={resolveRound}>Reveal Answer & Score</button>}

          {/* Round results */}
          {resolved&&roundResult&&<>
            <div style={{width:'100%',background:'#0a1628',borderRadius:9,padding:'10px 14px'}}>
              <div style={{fontSize:'0.52rem',letterSpacing:'0.15em',color:'#22c55e',textTransform:'uppercase',marginBottom:6}}>
                Answer: <strong style={{color:'#f5c518',fontSize:'0.84rem'}}>{currentFlag.name} {currentFlag.flag}</strong>
              </div>
              {roundResult.results.map((r,i)=>(
                <div key={i} style={{display:'flex',gap:8,alignItems:'center',padding:'5px 0',borderBottom:'1px solid #0f1e38'}}>
                  <span style={{flex:1,fontSize:'0.78rem',color:'#e8edf8'}}>{r.name}</span>
                  <span style={{fontSize:'0.72rem',color:r.correct?'#4ade80':'#f87171'}}>{r.answer||'—'}</span>
                  <span style={{fontSize:'0.78rem',fontFamily:"'Bebas Neue',sans-serif",color:'#f5c518',minWidth:52,textAlign:'right'}}>
                    {r.pts>0?'+'+r.pts+' pts':''} · {r.score}
                  </span>
                </div>
              ))}
            </div>
            {roundN>=TOTAL_ROUNDS
              ? <button className="btn-gold" onClick={endGame}>End Game & Show Results</button>
              : <button className="btn-blue" onClick={pushRound}>Next Round →</button>
            }
          </>}
        </>}

        {/* Live scoreboard always visible */}
        <div className="pq-scoreboard" style={{width:'100%'}}>
          <div className="pq-score-hdr">Scoreboard — Code: {code}</div>
          {sorted.map((p,i)=>(
            <div key={p.name} className="pq-score-row">
              <span className="pq-score-pos">#{i+1}</span>
              <span className="pq-score-name">{p.name} {p.answered?'✓':''}</span>
              <span className="pq-score-pts">{p.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ── GAME OVER ── */
  if(mode==='gameover'&&finalScores) return(
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title blue">GAME OVER</div>
      </div>
      <div className="game-home"><div className="over-card">
        <div className="over-tag" style={{color:'#38bdf8',border:'1px solid #0369a1'}}>FINAL RESULTS</div>
        <h1 className="over-h1">Flag Flash</h1>
        <div className="pq-scoreboard" style={{width:'100%'}}>
          {finalScores.map((p,i)=>(
            <div key={i} className="pq-score-row">
              <span className="pq-score-pos">{i===0?'🏆':i===1?'🥈':i===2?'🥉':('#'+(i+1))}</span>
              <span className="pq-score-name">{p.name}</span>
              <span className="pq-score-pts">{p.score}</span>
            </div>
          ))}
        </div>
        <button className="btn-blue" onClick={returnLobby}>Play Again</button>
        <button className="btn-gold" onClick={onBack}>Back to Menu</button>
      </div></div>
    </div>
  );

  return null;
}
